// The hold effect recipes, mockup round 043 (R924).
//
// One entry per catalog code the owner picked for a hold, keyed by the code
// of `data/contracts/effects.toml`. `effect-styles.ts` prefers this map over
// the row's family, so a code that is not here yet draws its family and the
// map fills in one card at a time. All twenty picked cards are here: the
// sixteen of R924 and the four the owner added later (R960).
//
// **A recipe reads its figures out of the row and holds no literal.** The
// row carries `periodSec`, `radiusPx`, `alpha` and `count`, plus the six
// figure fields `radius2Px`, `widthPx`, `lengthPx`, `alpha2`, `speed` and
// `count2`; `data/presentation/effects.toml` names what each one means for
// each card. A PROPORTION is not a figure: how far a breath opens a radius,
// how flat a ring of pips lies, or how wide an arc opens, stays here as a
// module constant, the way `RAY_INNER` does in the kit. A hold stands still
// and has no heading, so `c.heading` is 0 and a card of this round reads it
// for nothing.
//
// **A recipe is a pure function of the clock** (R915). It reads `c.t` and
// nothing else that moves, it keeps no state between frames, and it
// scatters with `grain(id, k)`. It allocates nothing (R131), and it clamps
// every alpha through `fill()` or `stroke()`.
//
// The hold's mark draws in the `holdeffects` slot, under every wake, hull
// and round (R914). The hold's own cartouche, anchor ring and coverage ring
// draw in the `sites` slot BELOW it, so a glow under the cartouche shows
// through only where the cartouche is transparent. Every card of this round
// therefore puts its marks round the cartouche and not on its face.
//
// **Three things the canvas card has and the engine has not.** A card of
// the mockup page paints with the 2D context; a recipe packs instances.
//
//   - A wide soft ELLIPSE. A quad carries one scale, so an aurora band and
//     a kraken roll are drawn as a chain of round `effect:fog` cells laid
//     along the long axis (`smear`).
//   - A filled WEDGE. `lighthouse` draws each wedge as one arc whose radius
//     is half the reach and whose width is the whole reach, which is an
//     annulus with an inner radius of zero.
//   - A filled POLYGON. `hold_compass` draws the star's outline as a closed
//     run of segments and states the faint inside with one disc of the
//     short points, because no batch fills a path.
//
// This is a leaf (`leaves.test.ts`): it imports the kit and the generated
// pack, and never an engine.

import { EFFECT_LIMITS } from '../gen/presentation';
import {
  TAU, bezier2, breathe, cellScale, cycle, effectCellKey, fill, grain, mixWhite, stroke,
  type EffectCtx, type EffectRecipeFn, type EffectRow,
} from './effect-recipe-kit';
import type { SpriteEntry } from '../fast/layers/batches';

// ---------------------------------------------------------------------------
// The helpers this round needs and the kit has not
// ---------------------------------------------------------------------------

/**
 * One soft cell of the atlas, tinted and scaled to a radius in world px.
 *
 * A call with no radius and no alpha writes nothing, so a grain that is
 * dark this frame costs the batch no instance.
 */
function puff(
  c: EffectCtx, e: SpriteEntry, x: number, y: number, r: number,
  color: number, a: number, flags: number,
): void {
  if (!(r > 0) || !(a > 0)) return;
  c.quads.sprite(x, y, 0, e, color, a, cellScale(e, r), flags);
}

/**
 * One short hump at `at` in a round of 0 to 1, of width `w`.
 *
 * It is the `hump` helper of the mockup page: the cards that light a pip in
 * sequence read it, and the round wraps, so the pip at 0 lights again the
 * moment the round closes.
 */
function hump(u: number, at: number, w: number): number {
  if (!(w > 0)) return 0;
  const d = ((u - at) % 1 + 1) % 1;
  const e = Math.min(d, 1 - d);
  return Math.exp(-(e / w) * (e / w));
}

/**
 * How far apart the cells of a chain stand, in cell radii.
 *
 * A soft cell falls to nothing at its own radius, so a step over about 0.8
 * leaves a gap and the oblong reads as a row of dots.
 */
const SMEAR_STEP = 0.7;
/** What share of the band's alpha one cell of a chain takes. */
const SMEAR_SHARE = 0.55;

/**
 * How many soft cells of radius `cellR` one oblong takes.
 *
 * The count comes from the ROW and not from the breath, so a band keeps one
 * instance count while it swells and the cap of the layer is a fixed
 * number.
 */
function smearCount(halfLen: number, cellR: number): number {
  if (!(cellR > 0)) return 1;
  return 1 + 2 * Math.ceil(Math.max(0, halfLen - cellR) / (SMEAR_STEP * cellR));
}

/**
 * One soft oblong: `n` cells of radius `cellR` laid along the axis
 * `cos`,`sin`, out to `halfLen` at each end.
 *
 * The cells overlap, so each one takes a share of the alpha the caller
 * states and the middle of the oblong reads at about that alpha.
 */
function smear(
  c: EffectCtx, e: SpriteEntry, cx: number, cy: number,
  cos: number, sin: number, halfLen: number, cellR: number, n: number,
  color: number, a: number, flags: number,
): void {
  const span = Math.max(0, halfLen - cellR);
  const half = (n - 1) / 2;
  const share = n > 1 ? SMEAR_SHARE : 1;
  for (let k = 0; k < n; k++) {
    const d = half > 0 ? ((k - half) / half) * span : 0;
    puff(c, e, cx + cos * d, cy + sin * d, cellR, color, a * share, flags);
  }
}

// ---------------------------------------------------------------------------
// The proportions of the cards
// ---------------------------------------------------------------------------
//
// Every figure in world px or in seconds comes from the row. What is here
// is the shape of one animation: a share, a squash, an angle or a ratio the
// owner does not tune.

/** 043 card 01. The lantern's radius at the bottom of the breath. */
const LANTERN_REST = 0.86;
/** 043 card 01. How far the breath opens the lantern's radius. */
const LANTERN_SWELL = 0.28;
/** 043 card 01. The lantern's alpha at the bottom of the breath. */
const LANTERN_FLOOR = 0.5;

/** 043 card 02. How far the breath widens a band, in half widths. */
const BAND_SWELL = 0.19;
/** 043 card 02. A band's half height at the bottom of the breath. */
const BAND_THIN = 0.7;
/** 043 card 02. How far a band rides up and down, in band gaps. */
const BAND_BOB = 0.78;
/** 043 card 02. A band's alpha at the bottom of the breath. */
const BAND_FLOOR = 0.35;
/**
 * 043 card 02. How wide the soft cell of a band is, in half heights.
 *
 * The card paints one gradient ellipse whose short half is the half height.
 * A round cell of exactly that radius needs a chain twice as long to read
 * as one band, so the cell stands a little wider than the card's ellipse
 * and the chain stays short.
 */
const BAND_FAT = 1.6;

/** 043 card 04. The smallest grain, in widest grains. */
const GLITTER_FLOOR = 0.44;

/** 043 card 06. The track's alpha, in pip alphas. */
const TRACK_SHARE = 0.4;
/** 043 card 06. How flat the orbit lies. */
const ORBIT_FLAT = 0.55;
/** 043 card 06. A pip's core, in pip glow radii. */
const PIP_CORE = 0.25;

/** 043 card 09. The widest wedge's half angle, in radians. */
const BEAM_HALF = 0.30;
/** 043 card 09. How much narrower the next wedge stands, in radians. */
const BEAM_STEP = 0.09;
/** 043 card 09. The widest wedge's alpha, in row alphas. */
const BEAM_FLOOR = 0.5;
/** 043 card 09. How much brighter the next wedge stands, in row alphas. */
const BEAM_RISE = 0.25;

/** 043 card 10. Where the first strand stands, in radians. */
const KELP_TURN = 0.7;
/** 043 card 10. How far behind the next strand sways, in radians. */
const KELP_LAG = 1.3;
/** 043 card 10. How flat the ring of feet lies. */
const KELP_FLAT = 0.7;
/** 043 card 10. How far under the centre a foot sits, in strand heights. */
const KELP_FOOT = 0.125;
/** 043 card 10. The bend point's sway, in tip sways. */
const KELP_BEND_SWAY = 0.59;
/** 043 card 10. The bend point's height, in strand heights. */
const KELP_BEND_RISE = 0.53;
/** 043 card 10. The straight pieces one strand is drawn with. */
const KELP_STEPS = 4;

/** 043 card 12. The halo's radius at the bottom of the breath. */
const HALO_REST = 0.88;
/** 043 card 12. How far the breath opens the halo's radius. */
const HALO_SWELL = 0.22;
/** 043 card 12. The halo's alpha at the bottom of the breath. */
const HALO_FLOOR = 0.55;

/** 043 card 14. The ring's alpha, in pip alphas. */
const CHAIN_RING = 0.36;
/** 043 card 14. A dark pip's alpha, in pip alphas. */
const CHAIN_REST = 0.44;
/** 043 card 14. A dark pip's radius, in pip glow radii. */
const CHAIN_CORE = 0.25;
/** 043 card 14. How far a lit pip's core swells, in pip glow radii. */
const CHAIN_SWELL = 0.15;
/** 043 card 14. How wide the lit window is, in rounds. */
const CHAIN_HUMP = 0.045;
/** 043 card 14. Under this the glow of a pip is not drawn at all. */
const CHAIN_LIT = 0.2;

/** 043 card 15. How far a bubble leaves the mouth across. */
const BUBBLE_SPREAD = 0.9;
/** 043 card 15. How flat the mouth of the column lies. */
const BUBBLE_FLAT = 0.3;
/** 043 card 15. How far under the centre the mouth sits, in reaches. */
const BUBBLE_FOOT = 0.4;
/** 043 card 15. How far a bubble wanders across, in widest bubbles. */
const BUBBLE_WOBBLE = 1.25;
/** 043 card 15. How fast a bubble wanders, in radians a life. */
const BUBBLE_RATE = 6;
/** 043 card 15. The smallest bubble, in widest bubbles. */
const BUBBLE_FLOOR = 0.42;

/** 043 card 19. How flat the field of grains lies. */
const FIELD_FLAT = 0.78;
/** 043 card 19. How far a grain drifts out over its life. */
const FIELD_DRIFT = 0.06;
/** 043 card 19. Over this hash a grain wears a glint. */
const FIELD_GLINT = 0.82;
/** 043 card 19. The smallest grain, in widest grains. */
const FIELD_FLOOR = 0.375;
/** 043 card 19. The shortest glint arm, in longest arms. */
const GLINT_FLOOR = 0.39;

/** 043 card 20. How wide the first beat is, in rounds. */
const BEAT_ONE_W = 0.045;
/** 043 card 20. Where the second beat falls, in rounds. */
const BEAT_TWO_AT = 0.15;
/** 043 card 20. How wide the second beat is, in rounds. */
const BEAT_TWO_W = 0.05;
/** 043 card 20. How strong the second beat is. */
const BEAT_TWO = 0.62;
/** 043 card 20. How far a beat opens the ring. */
const BEAT_RING = 0.42;
/** 043 card 20. How far a beat opens the glow. */
const BEAT_GLOW = 0.5;
/** 043 card 20. The ring's alpha between two beats, in top alphas. */
const BEAT_REST = 0.43;

/** 043 card 22. How flat the bed of embers lies. */
const EMBER_FLAT = 0.34;
/** 043 card 22. How far an ember rises over its life, in bed depths. */
const EMBER_RISE = 0.38;
/** 043 card 22. The smallest ember glow, in widest glows. */
const EMBER_FLOOR = 0.6;
/** 043 card 22. An ember's core, in glow radii. */
const EMBER_CORE = 0.16;

/** 043 card 25. How flat the ring of rolls lies. */
const ROLL_FLAT = 0.6;
/** 043 card 25. The swell's period, in row periods. */
const ROLL_BEAT = 0.7;
/** 043 card 25. A roll's long half at rest, in long halves. */
const ROLL_LONG = 0.8;
/** 043 card 25. A roll's short half at rest, in short halves. */
const ROLL_SHORT = 0.765;
/** 043 card 25. A roll's alpha at rest. */
const ROLL_FLOOR = 0.7;

/** 043 card 26. How flat the claim lies. */
const PULL_FLAT = 0.8;
/** 043 card 26. How sharply a grain goes out at the cartouche. */
const PULL_OUT = 2.2;
/** 043 card 26. How sharply a grain comes in at the claim edge. */
const PULL_IN = 8;
/** 043 card 26. The smallest grain, in widest grains. */
const PULL_FLOOR = 0.474;

/** 043 card 23. Where the first pip of the crown stands, in radians. */
const CROWN_TOP = -Math.PI / 2;
/** 043 card 23. How wide the lit window is, in rounds. */
const CROWN_HUMP = 0.06;
/** 043 card 23. Under this the glow of a pip is not drawn at all. */
const CROWN_LIT = 0.15;
/** 043 card 23. A dark pip's core, in widest cores. */
const CROWN_CORE = 0.613;
/** 043 card 23. A dark pip's alpha, in row alphas. */
const CROWN_REST = 0.489;

/** 043 card 28. The lit piece of one dash, in whole dash steps. */
const SPOKE_ON = 0.375;

/** 043 card 29. Where the arcs open, in radians. */
const ECHO_AT = -0.30;
/** 043 card 29. An arc's half sweep, in radians. */
const ECHO_HALF = 0.62;
/** 043 card 29. How fast a new arc comes up, in rounds. */
const ECHO_RAMP = 0.08;

// ---------------------------------------------------------------------------
// The twenty recipes
// ---------------------------------------------------------------------------

/**
 * The hold recipes, by catalog code.
 *
 * Every code the owner picked in mockup round 043 has its own function, and
 * the comment over each one names the card it comes from.
 */
export const RECIPES: Readonly<Record<string, EffectRecipeFn>> = {
  /**
   * 043 card 01. One soft glow lies over the cartouche and breathes. The
   * radius and the alpha open together.
   */
  beacon_lantern(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('disc'));
    const k = breathe(c.t, row.periodSec, c.phase);
    puff(c, e, c.x, c.y, row.radiusPx * (LANTERN_REST + LANTERN_SWELL * k),
      c.color, fill(row.alpha * (LANTERN_FLOOR + (1 - LANTERN_FLOOR) * k)) * c.alpha, 0);
  },

  /**
   * 043 card 02. Faint bands stand over the hold. Each band drifts toward
   * white and back, and it rides up and down on its own breath.
   */
  aurora(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('fog'));
    const n = smearCount(row.radiusPx, BAND_FAT * row.widthPx);
    for (let k = 0; k < row.count; k++) {
      const p = c.phase + k / row.count;
      const b = breathe(c.t, row.periodSec, p);
      const ink = mixWhite(c.color, EFFECT_LIMITS.maxDriftTowardWhite * b);
      const cy = c.y - row.radius2Px + k * row.lengthPx + (b - 0.5) * BAND_BOB * row.lengthPx;
      smear(c, e, c.x, cy, 1, 0,
        row.radiusPx * (1 + BAND_SWELL * b),
        BAND_FAT * row.widthPx * (BAND_THIN + (1 - BAND_THIN) * b), n, ink,
        fill(row.alpha * (BAND_FLOOR + (1 - BAND_FLOOR) * b)) * c.alpha, 0);
    }
  },

  /**
   * 043 card 04. Grains twinkle inside the coverage reach, each one once
   * over the period.
   */
  sea_glitter(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('disc'));
    for (let k = 0; k < row.count; k++) {
      const age = cycle(c.t, row.periodSec, c.phase + grain(c.id, 3 * k + 1));
      const h = grain(c.id, 3 * k + 2);
      const ang = h * TAU;
      const d = Math.sqrt(grain(c.id, 3 * k + 3)) * row.radiusPx;
      puff(c, e, c.x + Math.cos(ang) * d, c.y + Math.sin(ang) * d,
        row.widthPx * (GLITTER_FLOOR + (1 - GLITTER_FLOOR) * h), c.color,
        fill(row.alpha * Math.sin(Math.PI * age)) * c.alpha, c.add);
    }
  },

  /**
   * 043 card 06. Pips circle the hold on one faint track, one revolution a
   * period. The track lies flat, so the pips read as an orbit.
   */
  orbiting_sentries(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('disc'));
    c.rings.ring(c.x, c.y, row.radiusPx, row.widthPx, c.color,
      stroke(row.alpha * TRACK_SHARE) * c.alpha);
    for (let k = 0; k < row.count; k++) {
      const th = cycle(c.t, row.periodSec, c.phase + k / row.count) * TAU;
      const px = c.x + Math.cos(th) * row.radiusPx;
      const py = c.y + Math.sin(th) * row.radiusPx * ORBIT_FLAT;
      puff(c, e, px, py, row.radius2Px, c.color, fill(row.alpha) * c.alpha, 0);
      puff(c, e, px, py, row.radius2Px * PIP_CORE, c.color,
        fill(row.alpha2) * c.alpha, c.add);
    }
  },

  /**
   * 043 card 09. One pale beam stands out of the hold and turns one
   * revolution a period. The beam is `count` nested wedges: each one is
   * narrower and brighter than the one under it.
   */
  lighthouse(row: EffectRow, c: EffectCtx): void {
    const th = cycle(c.t, row.periodSec, c.phase) * TAU;
    for (let k = 0; k < row.count; k++) {
      const hw = BEAM_HALF - k * BEAM_STEP;
      if (hw <= 0) continue;
      // An arc of half the reach, as wide as the whole reach, is an
      // annulus whose inner radius is zero: the wedge of the card.
      c.rings.arc(c.x, c.y, row.radiusPx / 2, row.radiusPx, th - hw, 2 * hw,
        c.color, fill(row.alpha * (BEAM_FLOOR + BEAM_RISE * k)) * c.alpha);
    }
    c.segs.line(c.x, c.y,
      c.x + Math.cos(th) * row.radiusPx, c.y + Math.sin(th) * row.radiusPx,
      row.widthPx, c.color, stroke(row.alpha2) * c.alpha);
  },

  /**
   * 043 card 10. Strands stand round the hold and sway. Each strand is a
   * curve drawn as short straight pieces, and it carries `count2` beads.
   */
  kelp_garden(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('disc'));
    const a = stroke(row.alpha) * c.alpha;
    const ab = fill(row.alpha2) * c.alpha;
    for (let i = 0; i < row.count; i++) {
      const ang = (i / row.count) * TAU + KELP_TURN;
      const bx = c.x + Math.cos(ang) * row.radiusPx;
      const by = c.y + Math.sin(ang) * row.radiusPx * KELP_FLAT + KELP_FOOT * row.lengthPx;
      const sway = 2 * breathe(c.t, row.periodSec, c.phase + (i * KELP_LAG) / TAU) - 1;
      const qx = bx + sway * KELP_BEND_SWAY * row.radius2Px;
      const qy = by - KELP_BEND_RISE * row.lengthPx;
      const tx = bx + sway * row.radius2Px;
      const ty = by - row.lengthPx;
      let px = bx, py = by;
      for (let s = 1; s <= KELP_STEPS; s++) {
        const u = s / KELP_STEPS;
        const nx = bezier2(bx, qx, tx, u), ny = bezier2(by, qy, ty, u);
        c.segs.line(px, py, nx, ny, row.widthPx, c.color, a);
        px = nx; py = ny;
      }
      for (let j = 1; j <= row.count2; j++) {
        const u = j / (row.count2 + 1);
        puff(c, e, bezier2(bx, qx, tx, u), bezier2(by, qy, ty, u),
          row.widthPx, c.color, ab, c.add);
      }
    }
  },

  /**
   * 043 card 12. One wide soft disc lies under the whole claim and
   * breathes. Card 01 keeps its glow on the cartouche; this one fills the
   * ground.
   */
  hold_halo(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('disc'));
    const k = breathe(c.t, row.periodSec, c.phase);
    puff(c, e, c.x, c.y, row.radiusPx * (HALO_REST + HALO_SWELL * k),
      c.color, fill(row.alpha * (HALO_FLOOR + (1 - HALO_FLOOR) * k)) * c.alpha, 0);
  },

  /**
   * 043 card 14. White pips lie on the anchor ring and light in turn, one
   * round a period. A dark pip stays on the ring, so the chain reads with
   * the clock held.
   */
  pearl_chain(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('disc'));
    const u = cycle(c.t, row.periodSec, c.phase);
    c.rings.ring(c.x, c.y, row.radiusPx, row.widthPx, c.color,
      stroke(row.alpha * CHAIN_RING) * c.alpha);
    for (let k = 0; k < row.count; k++) {
      const at = k / row.count;
      const lit = hump(u, at, CHAIN_HUMP);
      const th = at * TAU;
      const px = c.x + Math.cos(th) * row.radiusPx;
      const py = c.y + Math.sin(th) * row.radiusPx;
      if (lit > CHAIN_LIT) {
        puff(c, e, px, py, row.radius2Px * lit, c.color,
          fill(row.alpha2 * lit) * c.alpha, 0);
      }
      puff(c, e, px, py, row.radius2Px * (CHAIN_CORE + CHAIN_SWELL * lit), c.color,
        fill(row.alpha * (CHAIN_REST + (1 - CHAIN_REST) * lit)) * c.alpha, c.add);
    }
  },

  /**
   * 043 card 15. White bubbles leave the mouth of the hold a few at a time
   * and rise off the cartouche.
   */
  bubble_column(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('disc'));
    for (let k = 0; k < row.count; k++) {
      const age = cycle(c.t, row.periodSec, c.phase + grain(c.id, 3 * k + 1));
      const h = grain(c.id, 3 * k + 2);
      const ang = h * TAU;
      const d = Math.sqrt(grain(c.id, 3 * k + 3)) * row.radiusPx;
      const wob = Math.sin((age + h) * BUBBLE_RATE) * BUBBLE_WOBBLE * row.widthPx;
      puff(c, e,
        c.x + Math.cos(ang) * d * BUBBLE_SPREAD + wob,
        c.y + BUBBLE_FOOT * row.radiusPx + Math.sin(ang) * d * BUBBLE_FLAT
          - age * row.lengthPx,
        row.widthPx * (BUBBLE_FLOOR + (1 - BUBBLE_FLOOR) * h), c.color,
        fill(row.alpha * Math.sin(Math.PI * age)) * c.alpha, c.add);
    }
  },

  /**
   * 043 card 18. A faint star lies under the hold and turns one revolution
   * a period. `count` points alternate between the long reach and the short
   * one. No batch fills a path, so the faint inside is one disc of the
   * short points and the outline is a closed run of segments.
   */
  hold_compass(row: EffectRow, c: EffectCtx): void {
    const turn = cycle(c.t, row.periodSec, c.phase) * TAU;
    c.rings.disc(c.x, c.y, row.radius2Px, c.color, fill(row.alpha2) * c.alpha);
    const a = stroke(row.alpha) * c.alpha;
    let px = 0, py = 0;
    for (let k = 0; k <= row.count; k++) {
      const th = turn + (k / row.count) * TAU;
      const rr = k % 2 ? row.radius2Px : row.radiusPx;
      const nx = c.x + Math.cos(th) * rr, ny = c.y + Math.sin(th) * rr;
      if (k > 0) c.segs.line(px, py, nx, ny, row.widthPx, c.color, a);
      px = nx; py = ny;
    }
  },

  /**
   * 043 card 19. Grains twinkle over the whole claim and drift out slowly.
   * The brightest of them wear a four-point glint.
   */
  star_field(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('disc'));
    const g = c.sprite(effectCellKey('glint'));
    for (let k = 0; k < row.count; k++) {
      const age = cycle(c.t, row.periodSec, c.phase + grain(c.id, 3 * k + 1));
      const h = grain(c.id, 3 * k + 2);
      const h2 = grain(c.id, 3 * k + 3);
      const ang = h * TAU;
      const d = Math.sqrt(h2) * row.radiusPx * (1 + FIELD_DRIFT * age);
      const f = Math.sin(Math.PI * age);
      const px = c.x + Math.cos(ang) * d;
      const py = c.y + Math.sin(ang) * d * FIELD_FLAT;
      puff(c, e, px, py, row.widthPx * (FIELD_FLOOR + (1 - FIELD_FLOOR) * h),
        c.color, fill(row.alpha * f) * c.alpha, c.add);
      if (h2 > FIELD_GLINT) {
        puff(c, g, px, py, row.radius2Px * (GLINT_FLOOR + (1 - GLINT_FLOOR) * f),
          c.color, fill(row.alpha2 * f) * c.alpha, c.add);
      }
    }
  },

  /**
   * 043 card 20. The anchor ring swells with a double beat once a period
   * and then rests. The ring never goes out, so the mark stands with the
   * clock held.
   */
  heartbeat(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('disc'));
    const u = cycle(c.t, row.periodSec, c.phase);
    const k = Math.min(1,
      hump(u, 0, BEAT_ONE_W) + BEAT_TWO * hump(u, BEAT_TWO_AT, BEAT_TWO_W));
    c.rings.ring(c.x, c.y, row.radiusPx * (1 + BEAT_RING * k), row.widthPx, c.color,
      stroke(row.alpha2 * (BEAT_REST + (1 - BEAT_REST) * k)) * c.alpha);
    puff(c, e, c.x, c.y, row.radiusPx * (1 + BEAT_GLOW * k), c.color,
      fill(row.alpha * k) * c.alpha, 0);
  },

  /**
   * 043 card 22. Small embers glow on the ground under the hold, rise a
   * little and fade out.
   */
  ember_bed(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('disc'));
    for (let k = 0; k < row.count; k++) {
      const age = cycle(c.t, row.periodSec, c.phase + grain(c.id, 3 * k + 1));
      const h = grain(c.id, 3 * k + 2);
      const ang = h * TAU;
      const d = Math.sqrt(grain(c.id, 3 * k + 3)) * row.radiusPx;
      const f = Math.sin(Math.PI * age);
      const px = c.x + Math.cos(ang) * d;
      const py = c.y + row.lengthPx + Math.sin(ang) * d * EMBER_FLAT
        - age * EMBER_RISE * row.lengthPx;
      puff(c, e, px, py, row.radius2Px * (EMBER_FLOOR + (1 - EMBER_FLOOR) * h),
        c.color, fill(row.alpha2 * f) * c.alpha, 0);
      puff(c, e, px, py, row.radius2Px * EMBER_CORE, c.color,
        fill(row.alpha * f) * c.alpha, c.add);
    }
  },

  /**
   * 043 card 25. Neutral fog rolls turn slowly round the hold at a low
   * alpha. One roll is an oblong of soft cells laid along its own heading.
   */
  kraken_ink(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('fog'));
    const n = smearCount(row.radius2Px, row.widthPx);
    for (let k = 0; k < row.count; k++) {
      const p = c.phase + k / row.count;
      const th = cycle(c.t, row.periodSec, p) * TAU;
      const s = breathe(c.t, row.periodSec * ROLL_BEAT, p);
      const cos = Math.cos(th), sin = Math.sin(th);
      smear(c, e, c.x + cos * row.radiusPx, c.y + sin * row.radiusPx * ROLL_FLAT,
        cos, sin,
        row.radius2Px * (ROLL_LONG + (1 - ROLL_LONG) * s),
        row.widthPx * (ROLL_SHORT + (1 - ROLL_SHORT) * s), n, c.color,
        fill(row.alpha * (ROLL_FLOOR + (1 - ROLL_FLOOR) * s)) * c.alpha, 0);
    }
  },

  /**
   * 043 card 26. Grains drift in from the claim edge toward the hold and go
   * out at the cartouche.
   */
  tidal_pull(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('disc'));
    for (let k = 0; k < row.count; k++) {
      const age = cycle(c.t, row.periodSec, c.phase + grain(c.id, 3 * k + 1));
      const h = grain(c.id, 3 * k + 2);
      const ang = h * TAU;
      const d0 = Math.sqrt(grain(c.id, 3 * k + 3)) * row.radiusPx;
      const d = d0 * (1 - age) + row.radius2Px * age;
      const f = Math.min(1, (1 - age) * PULL_OUT) * Math.min(1, age * PULL_IN);
      puff(c, e, c.x + Math.cos(ang) * d, c.y + Math.sin(ang) * d * PULL_FLAT,
        row.widthPx * (PULL_FLOOR + (1 - PULL_FLOOR) * h), c.color,
        fill(row.alpha * f) * c.alpha, c.add);
    }
  },

  /**
   * 043 card 03. Spokes stand out of the hold and turn one revolution a
   * period (R960). The row draws no fill, so the stroke reads `alpha2`.
   */
  guardian_rays(row: EffectRow, c: EffectCtx): void {
    const turn = cycle(c.t, row.periodSec, c.phase) * TAU;
    const a = stroke(row.alpha2) * c.alpha;
    for (let k = 0; k < row.count; k++) {
      const th = turn + (k / row.count) * TAU;
      const cos = Math.cos(th), sin = Math.sin(th);
      c.segs.line(
        c.x + cos * row.radius2Px, c.y + sin * row.radius2Px,
        c.x + cos * row.radiusPx, c.y + sin * row.radiusPx,
        row.widthPx, c.color, a,
      );
    }
  },

  /**
   * 043 card 23. Pips sit on the coverage ring and light in turn, one round
   * a period (R960). A dark pip keeps its core, so the crown reads with the
   * clock held.
   */
  crown(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('disc'));
    const u = cycle(c.t, row.periodSec, c.phase);
    for (let k = 0; k < row.count; k++) {
      const at = k / row.count;
      const lit = hump(u, at, CROWN_HUMP);
      const th = CROWN_TOP + at * TAU;
      const px = c.x + Math.cos(th) * row.radiusPx;
      const py = c.y + Math.sin(th) * row.radiusPx;
      if (lit > CROWN_LIT) {
        puff(c, e, px, py, row.radius2Px * lit, c.color,
          fill(row.alpha2 * lit) * c.alpha, 0);
      }
      c.rings.disc(px, py, row.widthPx * (CROWN_CORE + (1 - CROWN_CORE) * lit),
        c.color, fill(row.alpha * (CROWN_REST + (1 - CROWN_REST) * lit)) * c.alpha);
    }
  },

  /**
   * 043 card 24. One very faint wide pale disc drifts across the hold on a
   * flat ellipse, one pass a period (R960).
   */
  moonlight(row: EffectRow, c: EffectCtx): void {
    const e = c.sprite(effectCellKey('disc'));
    const th = cycle(c.t, row.periodSec, c.phase) * TAU;
    puff(c, e,
      c.x + Math.cos(th) * row.radius2Px,
      c.y + Math.sin(th) * row.widthPx - row.lengthPx,
      row.radiusPx, c.color, fill(row.alpha) * c.alpha, 0);
  },

  /**
   * 043 card 28. Dashed spokes run out of the hold and turn one revolution
   * a period (R960). The segment batch draws no dash, so one spoke is a run
   * of short pieces: `length_px` is the whole step and `SPOKE_ON` the lit
   * share of it.
   */
  spokes(row: EffectRow, c: EffectCtx): void {
    const turn = cycle(c.t, row.periodSec, c.phase) * TAU;
    const a = stroke(row.alpha2) * c.alpha;
    const steps = row.lengthPx > 0
      ? Math.ceil((row.radiusPx - row.radius2Px) / row.lengthPx) : 1;
    for (let k = 0; k < row.count; k++) {
      const th = turn + (k / row.count) * TAU;
      const cos = Math.cos(th), sin = Math.sin(th);
      for (let s = 0; s < steps; s++) {
        const d0 = row.radius2Px + s * row.lengthPx;
        const d1 = Math.min(row.radiusPx, d0 + row.lengthPx * SPOKE_ON);
        if (!(d1 > d0)) continue;
        c.segs.line(c.x + cos * d0, c.y + sin * d0,
          c.x + cos * d1, c.y + sin * d1, row.widthPx, c.color, a);
      }
    }
  },

  /**
   * 043 card 29. Arcs leave one side of the hold and fade out over the
   * claim. An arc is born on the inner radius and goes out at the reach.
   */
  echo_lines(row: EffectRow, c: EffectCtx): void {
    for (let k = 0; k < row.count; k++) {
      const u = cycle(c.t, row.periodSec, c.phase + k / row.count);
      const r = row.radius2Px + u * (row.radiusPx - row.radius2Px);
      c.rings.arc(c.x, c.y, r, row.widthPx, ECHO_AT - ECHO_HALF, 2 * ECHO_HALF,
        c.color, stroke(row.alpha2 * (1 - u) * Math.min(1, u / ECHO_RAMP)) * c.alpha);
    }
  },
};
